import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';
import styles from './Video.module.scss';
import { PlayInCircleIcon } from '../../../../lib/icons/PlayInCircleIcon';
import Button from '../../../common/Button/Button';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import PureHeading from '../../../common/Heading/PureHeading';
import Loader from '../../../common/Loader/Loader';
import LayoutContainer from '../../../common/Layout/Layout';
import { useViewContext } from '../../../../lib/context/ViewContext';
import { VideoChapter, VideoPlayerProps } from './VideoDetails.types';
import { replacePlaceholder } from '../../../../lib/utils/placeholders/replacePlaceholder';
import { BackArrow } from '../../../../lib/icons/BackArrow';
import { VideoChaptersSkeleton } from './VideoSkeletons';
import VideoJs from './VideoJs';
import { getCueStartTime, startVideo, isTimeInRange, formatTimes } from './Video.utils';
import { useId } from '../../../../lib/hooks/useId';
import { useConfig } from '../../../../lib/hooks/config';
import { ALLOWED_RICHTEXT_TAGS, VIDEO_PARAMS } from '../../../../lib/constants/courses';
import PureRichText from '../../../common/PureRichText/PureRichText';

const VideoPlayer = ({ lastUpdatePlaceholder, chaptersHeading, backButtonShow, backButtonLabel }: VideoPlayerProps) => {
  const textTrackRef = useRef<TextTrack | null>(null);
  const [chapters, setChapters] = useState<VideoChapter[] | null>(null);
  const [activeChapterStartTime, setActiveChapterStartTime] = useState<number | null>(null);
  const [changingChapterTime, setChangingChapterTime] = useState<null | number>(null);
  const { page } = useViewContext();
  const { video } = page;
  const lastUpdated = replacePlaceholder(video ?? {}, lastUpdatePlaceholder ?? '');
  const history = useHistory();
  const { search } = useLocation();
  const uniqueId = useId();
  const { brightcoveAccountId = '', brightcovePlayerId = '' } = useConfig() ?? {};

  const playerId = `bc_${video?.id}_${uniqueId}`;
  const [playerKey, setPlayerKey] = useState({ key: `${playerId}_0`, index: 0 });

  useLayoutEffect(() => {
    const isVideoJsInDocument = document.getElementById(playerId);
    if (video && isVideoJsInDocument && videojs.getPlayer(playerId)) {
      videojs.getPlayer(playerId)?.dispose();
    }
  }, []);

  useEffect(() => {
    const isVideoJsInDocument = document.getElementById(playerId);
    const changeKey = () => {
      setPlayerKey(prevKey => ({ key: `${playerId}_${prevKey.index + 1}`, index: prevKey.index + 1 }));
    };

    if (video && isVideoJsInDocument) {
      const searchParams = new URLSearchParams(search);
      const startTime = Number(searchParams.get(VIDEO_PARAMS.START)) || 0;
      const autoplay = searchParams.get(VIDEO_PARAMS.AUTOPLAY) === 'true';
      const player = videojs.getPlayer(playerId) ?? bc(playerId);

      player.on('loadedmetadata', () => {
        const filteredChapters = Array.from(player.textTracks()).filter(
          track =>
            track.kind === 'chapters' && (!track.language || track.language.startsWith(document.documentElement.lang)),
        );
        [textTrackRef.current] = filteredChapters;
        if (textTrackRef.current) {
          textTrackRef.current.oncuechange = () => {
            if (textTrackRef.current) {
              setActiveChapterStartTime(getCueStartTime(player.currentTime(), textTrackRef.current.cues));
              setChangingChapterTime(null);
            }
          };

          const simplifiedChapters = Array.from(textTrackRef.current.cues ?? []).map(cue => ({
            startTime: cue.startTime,
            endTime: cue.endTime,
            text: cue.text,
          }));

          setChapters(simplifiedChapters);
        } else {
          setChapters([]);
        }
        if (startTime) {
          player.currentTime(startTime);
          setActiveChapterStartTime(getCueStartTime(startTime, textTrackRef.current.cues));
        }
        if (autoplay) {
          startVideo(player);
        }
      });

      player.on('dispose', changeKey);
      player.on('error', () => {
        setChapters([]);
      });
    }

    return () => {
      const player = videojs.players[playerId];
      if (player) {
        player.off('dispose', changeKey);
        player.dispose();
      }
    };
  }, [playerKey]);

  if (!video?.id || !video?.videoId) {
    return null;
  }

  const handleChapterClick = (chapterTime: number) => {
    const player = videojs.getPlayer(playerId);
    if (player) {
      player?.currentTime(chapterTime);
      setChangingChapterTime(chapterTime);
      startVideo(player);
    }
  };

  return (
    <LayoutContainer>
      <div className={styles.top}>
        {backButtonShow && (
          <div className={styles.backButtonContainer}>
            <Button
              onClick={() => history.go(-1)}
              className={styles.backButton}
              variant="text"
              startIcon={<BackArrow />}
              classes={{ startIcon: styles.backButtonIcon }}
            >
              {backButtonLabel}
            </Button>
          </div>
        )}
        {lastUpdated && <p className={styles.lastUpdated}>{lastUpdated}</p>}
      </div>
      <div className={styles.videoContainer} key={playerKey.key}>
        <VideoJs
          componentId={playerId}
          playerId={brightcovePlayerId}
          accountId={brightcoveAccountId}
          videoId={video.videoId}
        />
      </div>
      <div>
        <PureHeading level={HeadingLevel.h1} as={HeadingLevel.h2} className={styles.videoTitle}>
          {video.name}
        </PureHeading>
        {!!video.description && (
          <div className={styles.videoDescription}>
            <PureRichText
              richtext={sanitizeHtml(video.description, {
                allowedTags: ALLOWED_RICHTEXT_TAGS,
                allowedAttributes: false,
              })}
            />
          </div>
        )}
      </div>
      <VideoChaptersSkeleton isLoading={chapters === null} isEmpty={chapters?.length === 0}>
        <div className={styles.chaptersContainer}>
          {chaptersHeading && (
            <PureHeading level={HeadingLevel.h2} as={HeadingLevel.h3} className={styles.chaptersHeading}>
              {chaptersHeading}
            </PureHeading>
          )}
          <ul className={styles.chaptersList}>
            {chapters?.map(chapter => (
              <li key={`${playerId}-${chapter.startTime}-${chapter.endTime}`} className={styles.chapterItem}>
                {isTimeInRange(chapter.startTime, chapter.endTime, activeChapterStartTime) && (
                  <PlayInCircleIcon aria-hidden className={styles.chapterActiveIcon} />
                )}
                {isTimeInRange(chapter.startTime, chapter.endTime, changingChapterTime) && (
                  <Loader containerProps={{ className: styles.chapterChangingIcon }} />
                )}
                <Button
                  variant="text"
                  className={styles.chapterItemButton}
                  onClick={() => handleChapterClick(chapter.startTime)}
                  disabled={isTimeInRange(chapter.startTime, chapter.endTime, [
                    activeChapterStartTime,
                    changingChapterTime,
                  ])}
                  disableRipple
                  focusVisibleClassName={styles.focus}
                >
                  <span>
                    {formatTimes(chapter.startTime)} - {chapter.text}
                  </span>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </VideoChaptersSkeleton>
    </LayoutContainer>
  );
};

export default VideoPlayer;
